import { Grid, Typography, useEventCallback } from '@mui/material';
import { Card, CardContent } from '@mui/material';
import { useFormik } from 'formik';
import { memo, useEffect, useMemo, useState } from 'react';
import ListHeader from 'shell/components/shared/list-header';
import PmiTextField from 'shell/components/shared/text-field';
import DomainValuesTable from '../act-domain-values-table';
import DomainValueFormModal from '../act-domain-value-modal';
import { getFatherDomainNameBasedOnType } from '../utils';
import { DateTime } from 'luxon';
import { toast } from 'react-toastify';
import { noop } from 'shell/utils/noop';
import { PmiButton } from 'shell/components/shared/button';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { ActDomainManagement } from 'shell/models/act-domain-management';
import { NewHttpResponseList } from 'shell/models/http-response';
import { transformerActDomains } from 'civil-status/pages/act-domain-management/utils/transform-act-domains';
import {
  ActDomainManagementVM,
  ActDomainManagmentResponseVM,
} from 'civil-status/view-models/act-domain-managment-list';
import {
  ActDomainFormVM,
  ActDomainManagementFormProps,
} from 'civil-status/view-models/act-domain-form';
import {
  ActValueDomainModalFormVM,
  ActValueDomainVM,
} from 'civil-status/view-models/act-value-domain-managment-list';
import { ValueDomainModalOpenType, validationSchema } from './constants';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import { authsObj } from 'civil-status/pages/act-domain-management/list/constants';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { ValueDomainModalType } from 'civil-status/view-models/act-value-domain';
import FilterTableProvider from 'shell/hooks/use-filter-table/provider';
import { initialValuesFilterTable } from '../act-domain-values-table/constants';
import { useUserProfile } from 'shell/hooks/use-user-profile';

const ActDomainManagementForm = ({
  title,
  disabled = false,
  isForDetail = false,
  isForCreate = false,
  isForEdit = false,
  initialValues,
  onSubmit = noop,
  onCancel = noop,
  valuesDomainListCount = 0,
}: ActDomainManagementFormProps) => {
  const [actDomains] = useHttpFetch<
    NewHttpResponseList<ActDomainManagement>,
    ActDomainManagmentResponseVM
  >(`${__ACT_API_URL__}/tipo-dominio-atti`, {
    onBootstrap: true,
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
      data: {},
    },
    transformer: transformerActDomains,
  });

  const auths = useActionAuthorization(authsObj);

  const actDomainsList: Array<ActDomainManagementVM> = useMemo(() => {
    return (actDomains.data as ActDomainManagmentResponseVM)?.actDomains || [];
  }, [actDomains?.data]);

  const [valuesDomainListBE, setValuesDomainListBE] = useState<Array<ActValueDomainVM>>([]);

  useEffect(() => {
    if (initialValues?.actDomainValues && initialValues?.actDomainValues?.length > 0) {
      setValuesDomainListBE(initialValues?.actDomainValues);
    }
  }, [initialValues?.actDomainValues]);

  const { userProfile } = useUserProfile();

  const actDomainForm = useFormik<ActDomainFormVM>({
    initialValues: initialValues || {
      entityBody: userProfile.datiLogin?.key?.codiceEnte || '',
      domainType: '',
      domainName: '',
      usageNote: '',
      actDomainValues: [],
      chooenValueActDomain: {
        description: '',
        displayOrderNumber: '',
        domainName: '',
        domainType: '',
        endValidityDate: '',
        entityBody: '',
        fatherDomainName: '',
        fatherDomainShortDescription: '',
        fatherDomainType: '',
        fatherDomainValue: '',
        lawAuthorizeArticle: '',
        precedingArticle: '',
        shortDescription: '',
        startValidityDate: '',
        statisticCode: '',
        value: '',
        valueAnsc: '',
        type: '',
      },
    },
    validationSchema: validationSchema,
    onSubmit,
    enableReinitialize: true,
  });
  const [listCount, setListCount] = useState<number>(0);

  useEffect(() => {
    if (isForCreate) {
      setListCount(actDomainForm.values?.actDomainValues?.length || 0);
    } else {
      setListCount(valuesDomainListCount);
    }
  }, [isForCreate, actDomainForm.values?.actDomainValues?.length]);

  const [domainValueModal, setDomainValueModal] = useState<ValueDomainModalType>({
    open: false,
    type: ValueDomainModalOpenType.Add,
  });

  const count = useMemo(() => {
    if (isForCreate || isForEdit) {
      return listCount;
    }
    return valuesDomainListCount;
  }, [isForCreate, isForEdit, listCount, valuesDomainListCount]);

  const onInsert = useEventCallback((formValues: ActValueDomainModalFormVM) => {
    if (isForDetail) return;

    const valueExists: boolean | undefined = actDomainForm?.values?.actDomainValues?.some(
      (item) => item?.value === formValues?.value
    );

    if (valueExists) {
      toast.error('Il valore di dominio inserito è già presente');
      setDomainValueModal({
        open: false,
        type: ValueDomainModalOpenType.Add,
      });
      return;
    }

    const data: Array<ActValueDomainVM> = [
      ...(actDomainForm.values?.actDomainValues || []),
      {
        ...formValues,
        startValidityDate: formValues?.startValidityDate
          ? formValues?.startValidityDate?.toFormat('dd/MM/yyyy')
          : '',
        endValidityDate: formValues?.endValidityDate
          ? formValues?.endValidityDate?.toFormat('dd/MM/yyyy')
          : '',
      },
    ];

    actDomainForm
      .setValues({
        ...(actDomainForm?.values || {}),
        actDomainValues: [...data],
      })
      .then(() => {
        setListCount((prev) => prev + 1);
      });

    setDomainValueModal({
      open: false,
      type: ValueDomainModalOpenType.Add,
    });
  });

  const setChoosenValueActDomain = useEventCallback((cell: ActValueDomainVM) => {
    actDomainForm.setValues({
      ...(actDomainForm?.values || {}),
      chooenValueActDomain: cell,
    });
  });

  const domainValueFormModalInitialValues: ActValueDomainModalFormVM = useMemo(() => {
    return {
      entityBody: actDomainForm.values?.entityBody || '',
      domainType: actDomainForm.values?.domainType || '',
      domainName: actDomainForm.values?.domainName || '',
      description: actDomainForm.values?.chooenValueActDomain?.description || '',
      displayOrderNumber: actDomainForm.values?.chooenValueActDomain?.displayOrderNumber || '',
      fatherDomainShortDescription:
        actDomainForm.values?.chooenValueActDomain?.fatherDomainShortDescription || '',
      fatherDomainType: actDomainForm.values?.chooenValueActDomain?.fatherDomainType || '',
      fatherDomainValue: actDomainForm.values?.chooenValueActDomain?.fatherDomainValue || '',
      lawAuthorizeArticle: actDomainForm.values?.chooenValueActDomain?.lawAuthorizeArticle || '',
      precedingArticle: actDomainForm.values?.chooenValueActDomain?.precedingArticle || '',
      shortDescription: actDomainForm.values?.chooenValueActDomain?.shortDescription || '',
      statisticCode: actDomainForm.values?.chooenValueActDomain?.statisticCode || '',
      value: actDomainForm.values?.chooenValueActDomain?.value || '',
      valueAnsc: actDomainForm.values?.chooenValueActDomain?.valueAnsc || '',
      type: actDomainForm.values?.chooenValueActDomain?.type || '',
      startValidityDate: actDomainForm?.values?.chooenValueActDomain?.startValidityDate
        ? DateTime.fromISO(
            DateTime.fromFormat(
              actDomainForm.values?.chooenValueActDomain?.startValidityDate || '',
              'dd/MM/yyyy'
            )?.toISO() || ''
          )
        : null,
      endValidityDate: actDomainForm?.values?.chooenValueActDomain?.endValidityDate
        ? DateTime.fromISO(
            DateTime.fromFormat(
              actDomainForm.values?.chooenValueActDomain?.endValidityDate || '',
              'dd/MM/yyyy'
            )?.toISO() || ''
          )
        : null,
      fatherDomainName: getFatherDomainNameBasedOnType(
        actDomainsList,
        actDomainForm.values?.chooenValueActDomain?.fatherDomainType
      ),
    };
  }, [
    actDomainForm.values?.entityBody,
    actDomainForm.values?.domainType,
    actDomainForm.values?.domainName,
    actDomainForm.values.chooenValueActDomain,
  ]);

  const onUpdate = useEventCallback((formValues: ActValueDomainModalFormVM) => {
    if (isForDetail) return;

    const data: Array<ActValueDomainVM> =
      actDomainForm.values?.actDomainValues?.map((item: ActValueDomainVM) => {
        if (item?.value === formValues?.value) {
          return {
            ...item,
            ...formValues,
            startValidityDate: formValues?.startValidityDate
              ? formValues?.startValidityDate?.toFormat('dd/MM/yyyy')
              : '',
            endValidityDate: formValues?.endValidityDate
              ? formValues?.endValidityDate?.toFormat('dd/MM/yyyy')
              : '',
          };
        }
        return item;
      }) || [];

    actDomainForm.setValues({
      ...(actDomainForm?.values || {}),
      actDomainValues: [...data],
    });

    setDomainValueModal({
      open: false,
      type: ValueDomainModalOpenType.Edit,
    });
  });

  const onDeleteFE = useEventCallback((cell: ActValueDomainVM) => {
    const data: Array<ActValueDomainVM> =
      actDomainForm.values?.actDomainValues?.filter((item: ActValueDomainVM) => {
        if (item?.value !== cell?.value) {
          return item;
        }
      }) || [];

    actDomainForm
      .setValues({
        ...(actDomainForm?.values || {}),
        actDomainValues: [...data],
      })
      .then(() => {
        setListCount((prev) => prev - 1);
      });

    setDomainValueModal({
      open: false,
      type: ValueDomainModalOpenType.Edit,
    });
  });

  const nextValue: number = useMemo(() => {
    const allValue = actDomainForm.values.actDomainValues?.map((item) =>
      Number.parseInt(item.value || '', 10)
    );

    if (allValue?.length) {
      const currentMax = (allValue || []).reduce((a, b) => Math.max(a, b));

      return currentMax ? currentMax + 1 : 0;
    }

    return 0;
  }, [actDomainForm.values?.actDomainValues]);

  return (
    <>
      <Card raised className="pmi-my-3">
        <form onSubmit={actDomainForm.handleSubmit}>
          <CardContent style={{ padding: '24px' }}>
            <Typography
              color="secondary.main"
              style={{
                fontSize: '24px',
              }}
            >
              {title}
            </Typography>

            <div style={{ padding: '8px 0 0 0' }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={2}>
                  <PmiTextField
                    label="Ente *"
                    id="entityBody"
                    name="entityBody"
                    fullWidth
                    disabled
                    type="text"
                    value={actDomainForm.values?.entityBody || ''}
                    onChange={actDomainForm.handleChange}
                    onBlur={() => actDomainForm.setFieldTouched('entityBody', true)}
                    error={
                      actDomainForm.touched.entityBody && Boolean(actDomainForm.errors.entityBody)
                    }
                    helperText={actDomainForm.errors.entityBody}
                    inputProps={{
                      maxLength: '5',
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <PmiTextField
                    label="Tipo dominio *"
                    id="domainType"
                    name="domainType"
                    fullWidth
                    disabled={disabled}
                    type="text"
                    value={actDomainForm.values?.domainType || ''}
                    onChange={actDomainForm.handleChange}
                    onBlur={() => actDomainForm.setFieldTouched('domainType', true)}
                    error={
                      actDomainForm.touched.domainType && Boolean(actDomainForm.errors.domainType)
                    }
                    helperText={actDomainForm.errors.domainType}
                    inputProps={{
                      maxLength: '5',
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={7}>
                  <PmiTextField
                    label="Nome dominio *"
                    id="domainName"
                    name="domainName"
                    fullWidth
                    type="text"
                    disabled={disabled}
                    value={actDomainForm.values?.domainName || ''}
                    onChange={actDomainForm.handleChange}
                    onBlur={() => actDomainForm.setFieldTouched('domainName', true)}
                    error={
                      actDomainForm.touched.domainName && Boolean(actDomainForm.errors.domainName)
                    }
                    helperText={actDomainForm.errors.domainName}
                    inputProps={{
                      maxLength: '250',
                    }}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={12}>
                  <PmiTextField
                    label="Note di utilizzo *"
                    id="usageNote"
                    name="usageNote"
                    fullWidth
                    multiline
                    rows={4}
                    type="text"
                    disabled={disabled}
                    value={actDomainForm.values?.usageNote || ''}
                    onChange={actDomainForm.handleChange}
                    onBlur={() => actDomainForm.setFieldTouched('usageNote', true)}
                    error={
                      actDomainForm.touched.usageNote && Boolean(actDomainForm.errors.usageNote)
                    }
                    helperText={actDomainForm.errors.usageNote}
                    inputProps={{
                      maxLength: '250',
                    }}
                  />
                </Grid>
              </Grid>
              {disabled ? (
                <>
                  <Typography
                    color="secondary.main"
                    style={{
                      fontSize: '26px',
                      marginTop: '16px',
                    }}
                  >
                    Valori dominio
                  </Typography>
                </>
              ) : (
                <ListHeader
                  addBtnAriaLabel="Aggiungi nuovo valore"
                  addBtnLabel="Aggiungi nuovo valore"
                  canAdd={auths.create || auths?.edit}
                  onAddBtnClick={() => {
                    actDomainForm.setValues({
                      ...(actDomainForm?.values || {}),
                      chooenValueActDomain: {},
                    });
                    setDomainValueModal({
                      open: true,
                      type: ValueDomainModalOpenType.Add,
                    });
                  }}
                  title="Valori dominio"
                />
              )}
            </div>
            <FilterTableProvider initialValues={initialValuesFilterTable}>
              <DomainValuesTable
                body={actDomainForm.values?.actDomainValues || []}
                count={count || 0}
                isForDetail={isForDetail}
                isForCreate={isForCreate}
                isForEdit={isForEdit}
                setDomainValueModal={setDomainValueModal}
                domainValueModal={domainValueModal}
                onDeleteFE={onDeleteFE}
                valuesDomainListBE={valuesDomainListBE}
                setChoosenValueActDomain={setChoosenValueActDomain}
              />
            </FilterTableProvider>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={2}>
                <PmiButton
                  variant="outlined"
                  disabled={isForDetail}
                  fullWidth
                  color="cdmBrandThird"
                  type="submit"
                >
                  Conferma
                </PmiButton>
              </Grid>
              <Grid item xs={12} md={2}>
                <PmiButton
                  fullWidth
                  variant="outlined"
                  color="cdmBrand"
                  type="button"
                  onClick={() => onCancel()}
                >
                  Annulla
                </PmiButton>
              </Grid>
            </Grid>
          </CardContent>
        </form>
      </Card>
      {domainValueModal.open ? (
        <DomainValueFormModal
          title={
            domainValueModal?.type === ValueDomainModalOpenType.Add
              ? 'Inserimento valore di dominio'
              : 'Visualizzazione valore di dominio'
          }
          onClose={() => {
            setDomainValueModal({
              open: false,
              type: ValueDomainModalOpenType.Add,
            });
          }}
          actDomainsList={actDomainsList}
          disabled={domainValueModal.type === ValueDomainModalOpenType.Read}
          initialValues={{
            ...domainValueFormModalInitialValues,
            value: domainValueFormModalInitialValues.value || nextValue?.toString() || '',
          }}
          onSubmit={(formValues: ActDomainFormVM) => {
            if (domainValueModal.type === ValueDomainModalOpenType.Add) {
              onInsert(formValues);
            }
            if (domainValueModal.type === ValueDomainModalOpenType.Edit) {
              onUpdate(formValues);
            }
          }}
          domainModal={domainValueModal}
        />
      ) : undefined}
    </>
  );
};

ActDomainManagementForm.displayName = 'Act Domain Management Form Component';
const ActDomainForm = memo(ActDomainManagementForm);
export default ActDomainForm;
